/**
 * Time labels for the notification feed rows. Pure + `now`-injectable so the
 * compact label can be pinned without freezing the clock.
 */

/**
 * Compact relative time ("now", "5m", "3h", "2d", "4w"), matching the native
 * Notifications screen. Empty string on an absent/unparseable date.
 */
export function timeAgo(iso: string | null, now: number = Date.now()): string {
  if (!iso) return '';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.floor((now - then) / 1000));
  if (secs < 45) return 'now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}

/**
 * A clock-style label for an activity row (Agent Detail's Activity feed): today →
 * time only ("9:03 AM"), earlier this week → weekday + time ("Mon 7:00 AM"), older →
 * a short date ("May 3"). Uses the runtime locale's own formats via `toLocale*`, so
 * 24h locales read "07:00" rather than "7:00 AM". `now` injectable for tests. Empty
 * string on an absent/unparseable date.
 */
export function activityTime(iso: string | null, now: number = Date.now(), locale?: string): string {
  if (!iso) return '';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const d = new Date(then);
  const time = d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const startMs = startOfToday.getTime();
  const dayMs = 86400 * 1000;
  if (then >= startMs) return time;
  if (then >= startMs - 6 * dayMs) return `${d.toLocaleDateString(locale, { weekday: 'short' })} ${time}`;
  return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

/**
 * The full localized date+time behind the compact label (the row's `title`
 * tooltip). Empty string on an absent/unparseable date.
 */
export function fullTimestamp(iso: string | null, locale?: string): string {
  if (!iso) return '';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  return new Date(then).toLocaleString(locale, { dateStyle: 'full', timeStyle: 'short' });
}
